import React, { useState, useCallback, useMemo } from 'react';
import { Text, View, TouchableOpacity, ScrollView } from 'react-native';
import { useUnistyles } from 'react-native-unistyles';
import { StyleSheet } from 'react-native-unistyles';
import FieldRenderer from '@/components/Forms/FieldRenderer';
import FieldTypePicker, { FIELD_TYPES } from '@/components/Forms/builder/FieldTypePicker';
import FieldOptionsSheet from '@/components/Forms/builder/FieldOptionsSheet';
import SectionOptionsSheet from '@/components/Forms/builder/SectionOptionsSheet';
import Unicon from '@/components/General/Unicon';
import IconButton from '@/components/General/IconButton';
import { createField, createSection, cloneSection, cloneField, collectFieldIds, flattenFields, ensureUniqueFieldId } from '@/utils/formsSchema';

/**
 * Build tab — assembles sections + fields with live preview and options editors.
 * Reads/writes the working `schema` via `onSchemaChange`. Reordering uses
 * up/down move buttons (works on both web and native).
 */
const FormBuildTab = ({ schema, onSchemaChange, loggedInUser, canEdit, formID, isPublic = false }) => {
    const { theme: { colors, fonts, shadows } } = useUnistyles();
    const [typePickerOpen, setTypePickerOpen] = useState(false);
    const [activeSectionId, setActiveSectionId] = useState(null);
    const [editingField, setEditingField] = useState(null);
    const [editingSection, setEditingSection] = useState(null);

    const sections = schema?.sections || [];

    const updateSchema = useCallback((newSections) => {
        onSchemaChange({ ...(schema || { version: 3, sections: [] }), sections: newSections });
    }, [schema, onSchemaChange]);

    const handleAddSection = useCallback(() => {
        const section = createSection({ title: 'New Section' });
        updateSchema([...sections, section]);
    }, [sections, updateSchema]);

    const handleAddField = useCallback((type) => {
        if (!activeSectionId) return;
        // Directory-dependent profile fields are never allowed on public
        // forms — belt-and-braces alongside the picker disable + save guard.
        // Signatures are fine: anonymous signers provide name/consent/ink.
        if (isPublic && type === 'profile') return;
        if (type === 'payment' && (flattenFields(schema).some((f) => f.type === 'payment') || sections.find((s) => s.id === activeSectionId)?.allowAddMore)) return;
        const typeLabel = FIELD_TYPES.find((ft) => ft.type === type)?.label || 'New Field';
        const field = createField({ type, label: typeLabel, sectionId: activeSectionId });
        // Two fields of the same type share a label → same derived fieldId —
        // force uniqueness against the live schema or their payload answers
        // would collide on the wire key.
        field.fieldId = ensureUniqueFieldId(field.fieldId, schema);
        updateSchema(
            sections.map((s) =>
                s.id === activeSectionId ? { ...s, fields: [...(s.fields || []), field] } : s
            )
        );
        // Open the options editor for the new field
        setTimeout(() => setEditingField(field), 50);
    }, [activeSectionId, sections, updateSchema, isPublic]);

    const handleFieldChange = useCallback((sectionId, fieldId, updatedField) => {
        updateSchema(
            sections.map((s) =>
                s.id === sectionId
                    ? { ...s, fields: (s.fields || []).map((f) => (f.id === fieldId ? updatedField : f)) }
                    : s
            )
        );
        // Keep the editing ref in sync so changes persist while the sheet is open
        setEditingField((prev) => (prev && prev.id === fieldId ? updatedField : prev));
    }, [sections, updateSchema]);

    const handleSectionChange = useCallback((sectionId, updatedSection) => {
        updateSchema(sections.map((s) => (s.id === sectionId ? updatedSection : s)));
        setEditingSection((prev) => (prev && prev.id === sectionId ? updatedSection : prev));
    }, [sections, updateSchema]);

    const moveSection = useCallback((index, direction) => {
        const newIndex = index + direction;
        if (newIndex < 0 || newIndex >= sections.length) return;
        const next = [...sections];
        const [moved] = next.splice(index, 1);
        next.splice(newIndex, 0, moved);
        updateSchema(next);
    }, [sections, updateSchema]);

    const moveField = useCallback((sectionId, index, direction) => {
        updateSchema(
            sections.map((s) => {
                if (s.id !== sectionId) return s;
                const fields = [...(s.fields || [])];
                const newIndex = index + direction;
                if (newIndex < 0 || newIndex >= fields.length) return s;
                const [moved] = fields.splice(index, 1);
                fields.splice(newIndex, 0, moved);
                return { ...s, fields };
            })
        );
    }, [sections, updateSchema]);

    const deleteField = useCallback((sectionId, fieldId) => {
        updateSchema(
            sections.map((s) =>
                s.id === sectionId
                    ? { ...s, fields: (s.fields || []).filter((f) => f.id !== fieldId) }
                    : s
            )
        );
    }, [sections, updateSchema]);

    const deleteSection = useCallback((sectionId) => {
        updateSchema(sections.filter((s) => s.id !== sectionId));
    }, [sections, updateSchema]);

    const duplicateField = useCallback((sectionId, fieldId) => {
        const section = sections.find((s) => s.id === sectionId);
        if (!section) return;
        const fIndex = (section.fields || []).findIndex((f) => f.id === fieldId);
        if (section.fields[fIndex]?.type === 'payment') return;
        if (fIndex === -1) return;
        const clone = cloneField(section.fields[fIndex], { sections });
        const fields = [...(section.fields || [])];
        fields.splice(fIndex + 1, 0, clone);
        updateSchema(sections.map((s) => (s.id === sectionId ? { ...s, fields } : s)));
    }, [sections, updateSchema]);

    const duplicateSection = useCallback((sectionId) => {
        const index = sections.findIndex((s) => s.id === sectionId);
        if (index === -1 || sections[index].fields?.some((f) => f.type === 'payment')) return;
        const clone = cloneSection(sections[index], { sections });
        const next = [...sections];
        next.splice(index + 1, 0, clone);
        updateSchema(next);
    }, [sections, updateSchema]);

    // All fields before a given section (for condition pickers)
    const fieldsBefore = useCallback((targetSectionId) => {
        const result = [];
        for (const s of sections) {
            if (s.id === targetSectionId) break;
            result.push(...(s.fields || []));
        }
        return result;
    }, [sections]);

    const editingFieldAllIds = useMemo(() => {
        if (!editingField) return [];
        return collectFieldIds({ sections }).filter((id) => id !== editingField.fieldId);
    }, [editingField, sections]);

    // All fields across the form, used to resolve condition field labels/values.
    const allFields = useMemo(() => flattenFields({ sections }), [sections]);

    // Build a human-readable summary of a section's show-condition rule.
    // Shows the referenced field's label (not its fieldId) and a value rendered
    // in the same vocabulary the user sees in the form.
    const conditionSummary = useCallback((condition) => {
        if (!condition) return '';
        const field = allFields.find((f) => f.fieldId === condition.fieldId);
        const fieldLabel = field?.label || condition.fieldId;
        let valueText;
        switch (field?.type) {
            case 'checkbox':
                valueText = condition.value === true || condition.value === 'true' ? 'on' : 'off';
                return `Shows when "${fieldLabel}" is ${valueText}`;
            case 'dropdown':
            case 'checkboxGroup': {
                const opts = (field?.config?.options || []).map((o) =>
                    typeof o === 'object' ? o : { label: String(o), value: String(o) }
                );
                const match = opts.find((o) => String(o.value) === String(condition.value));
                valueText = match?.label || condition.value;
                return `Shows when "${fieldLabel}" is "${valueText}"`;
            }
            case 'date':
            case 'datetime':
                valueText = condition.value
                    ? new Date(condition.value).toLocaleDateString()
                    : '';
                return `Shows when "${fieldLabel}" is "${valueText}"`;
            case 'time':
                valueText = condition.value
                    ? new Date(condition.value).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
                    : '';
                return `Shows when "${fieldLabel}" is "${valueText}"`;
            default:
                return `Shows when "${fieldLabel}" equals "${condition.value}"`;
        }
    }, [allFields]);

    return (
        <ScrollView style={styles.container} contentContainerStyle={styles.content} keyboardShouldPersistTaps="handled">
            {sections.length === 0 && (
                <View style={styles.emptyState}>
                    <Unicon name="document-text-outline" size={40} colorVariant="textTertiary" />
                    <Text style={styles.emptyTitle(colors, fonts)}>No sections yet</Text>
                    <Text style={styles.emptySubtext(colors)}>Add a section to start building your form.</Text>
                </View>
            )}

            {sections.map((section, sIndex) => {
                return (
                    <View key={section.id} style={styles.sectionCard(colors, shadows)}>
                        <View style={styles.sectionHeader}>
                            <View style={styles.sectionHeaderText}>
                                <Text style={styles.sectionTitle(colors, fonts)}>{section.title || 'Untitled Section'}</Text>
                                {!!section.description && (
                                    <Text style={styles.sectionDescription(colors)}>{section.description}</Text>
                                )}
                                {section.condition && (
                                    <Text style={styles.sectionCondition(colors)}>
                                        {conditionSummary(section.condition)}
                                    </Text>
                                )}
                            </View>
                            {canEdit && (
                                <View style={styles.sectionActions}>
                                    <IconButton
                                        iconName="chevron-up-outline"
                                        iconSize={18}
                                        type="textSecondary"
                                        disabled={sIndex === 0}
                                        onPress={() => moveSection(sIndex, -1)}
                                        tooltipText="Move section up"
                                    />
                                    <IconButton
                                        iconName="chevron-down-outline"
                                        iconSize={18}
                                        type="textSecondary"
                                        disabled={sIndex === sections.length - 1}
                                        onPress={() => moveSection(sIndex, 1)}
                                        tooltipText="Move section down"
                                    />
                                    <IconButton
                                        iconName="options-outline"
                                        iconSize={18}
                                        type="textSecondary"
                                        onPress={() => setEditingSection(section)}
                                        tooltipText="Section Settings"
                                        testID={`section-options-${section.id}`}
                                    />
                                    <IconButton
                                        iconName="copy-outline"
                                        iconSize={18}
                                        type="textSecondary"
                                        onPress={() => duplicateSection(section.id)}
                                        tooltipText="Duplicate this section"
                                        testID={`section-duplicate-${section.id}`}
                                    />
                                    <IconButton
                                        iconName="trash-outline"
                                        iconSize={18}
                                        type="error"
                                        onPress={() => deleteSection(section.id)}
                                        tooltipText="Delete section"
                                    />
                                </View>
                            )}
                        </View>

                        <View style={styles.fieldsList}>
                            {(section.fields || []).map((field, fIndex) => (
                                <View key={field.id} style={styles.fieldRow(colors)}>
                                    {canEdit && (
                                        <View style={styles.fieldReorder}>
                                            <IconButton
                                                iconName="chevron-up-outline"
                                                iconSize={14}
                                                type="textSecondary"
                                                disabled={fIndex === 0}
                                                onPress={() => moveField(section.id, fIndex, -1)}
                                            />
                                            <IconButton
                                                iconName="chevron-down-outline"
                                                iconSize={14}
                                                type="textSecondary"
                                                disabled={fIndex === (section.fields || []).length - 1}
                                                onPress={() => moveField(section.id, fIndex, 1)}
                                            />
                                        </View>
                                    )}
                                    <View style={styles.fieldPreviewWrap}>
                                        <View style={styles.fieldPreview}>
                                            <FieldRenderer
                                                field={field}
                                                value={field.defaultValue}
                                                onChange={() => {}}
                                                disabled
                                                loggedInUser={loggedInUser}
                                            />
                                        </View>
                                        {canEdit && (
                                            <View style={styles.fieldActionsOverlay}>
                                                <IconButton
                                                    iconName="create-outline"
                                                    iconSize={18}
                                                    type="textSecondary"
                                                    onPress={() => setEditingField(field)}
                                                    tooltipText="Edit field"
                                                    testID={`field-options-${field.fieldId}`}
                                                />
                                                <IconButton
                                                    iconName="copy-outline"
                                                    iconSize={18}
                                                    type="textSecondary"
                                                    onPress={() => duplicateField(section.id, field.id)}
                                                    tooltipText="Duplicate field"
                                                    testID={`field-duplicate-${field.fieldId}`}
                                                />
                                                <IconButton
                                                    iconName="close-outline"
                                                    iconSize={18}
                                                    type="error"
                                                    onPress={() => deleteField(section.id, field.id)}
                                                    tooltipText="Delete field"
                                                />
                                            </View>
                                        )}
                                    </View>
                                </View>
                            ))}

                            {(section.fields || []).length === 0 && (
                                <Text style={styles.noFieldsText(colors)}>No fields in this section yet.</Text>
                            )}

                            {canEdit && (
                                <TouchableOpacity
                                    style={styles.addFieldBtn(colors)}
                                    onPress={() => {
                                        setActiveSectionId(section.id);
                                        setTypePickerOpen(true);
                                    }}
                                    testID={`add-field-button-${section.id}`}
                                >
                                    <Unicon name="add-circle-outline" size={18} colorVariant="forms" />
                                    <Text style={styles.addFieldText(colors, fonts)}>Add Field</Text>
                                </TouchableOpacity>
                            )}
                        </View>
                    </View>
                );
            })}

            {canEdit && (
                <TouchableOpacity
                    style={styles.addSectionBtn(colors)}
                    onPress={handleAddSection}
                    testID="add-section-button"
                >
                    <Unicon name="add-outline" size={20} colorVariant="forms" />
                    <Text style={styles.addSectionText(colors, fonts)}>Add Section</Text>
                </TouchableOpacity>
            )}

            <FieldTypePicker
                isOpen={typePickerOpen}
                onClose={() => setTypePickerOpen(false)}
                onPick={handleAddField}
                paymentDisabled={allFields.some((f) => f.type === 'payment') || sections.find((s) => s.id === activeSectionId)?.allowAddMore}
                isPublic={isPublic}
            />

            <FieldOptionsSheet
                isOpen={!!editingField}
                onClose={() => setEditingField(null)}
                field={editingField}
                formID={formID}
                allFieldIds={editingFieldAllIds}
                onChange={(updated) => editingField && handleFieldChange(
                    sections.find((s) => (s.fields || []).some((f) => f.id === editingField.id))?.id,
                    editingField.id,
                    updated
                )}
            />

            <SectionOptionsSheet
                isOpen={!!editingSection}
                onClose={() => setEditingSection(null)}
                section={editingSection}
                fields={editingSection ? fieldsBefore(editingSection.id) : []}
                onChange={(updated) => editingSection && handleSectionChange(editingSection.id, updated)}
            />
        </ScrollView>
    );
};

const styles = StyleSheet.create((_theme) => ({
    container: { flex: 1 },
    content: {
        padding: 16,
        maxWidth: 720,
        alignSelf: 'center',
        width: '100%',
        paddingBottom: 100,
    },
    emptyState: { alignItems: 'center', justifyContent: 'center', paddingVertical: 60, gap: 8 },
    emptyTitle: (colors, fonts) => ({
        fontSize: 15,
        fontFamily: fonts.heading,
        color: colors.textSecondary,
    }),
    emptySubtext: (colors) => ({
        fontSize: 13,
        color: colors.textTertiary,
        textAlign: 'center',
    }),

    sectionCard: (colors, shadows) => ({
        backgroundColor: colors.surface,
        borderRadius: 12,
        marginBottom: 12,
        overflow: 'hidden',
        boxShadow: shadows.card,
    }),
    sectionHeader: { flexDirection: 'row', alignItems: 'flex-start', padding: 14, gap: 8 },
    sectionHeaderText: { flex: 1 },
    sectionTitle: (colors, fonts) => ({
        fontSize: 16,
        fontFamily: fonts.heading,
        color: colors.text,
    }),
    sectionDescription: (colors) => ({
        fontSize: 13,
        color: colors.textSecondary,
        marginTop: 2,
    }),
    sectionCondition: (colors) => ({
        fontSize: 11,
        color: colors.forms,
        marginTop: 4,
        fontStyle: 'italic',
    }),
    sectionActions: { flexDirection: 'row', gap: 2 },

    fieldsList: { paddingHorizontal: 14, paddingBottom: 14 },
    fieldRow: (colors) => ({
        flexDirection: 'row',
        alignItems: 'flex-start',
        paddingVertical: 6,
        borderTopWidth: 1,
        borderTopColor: colors.border,
    }),
    fieldReorder: { paddingTop: 8, gap: 2 },
    fieldPreviewWrap: { flex: 1, position: 'relative' },
    fieldPreview: { paddingLeft: 8, paddingRight: 104 },
    fieldActionsOverlay: { position: 'absolute', top: 6, right: 0, flexDirection: 'row', gap: 2, zIndex: 5 },

    noFieldsText: (colors) => ({
        fontSize: 13,
        color: colors.textTertiary,
        fontStyle: 'italic',
        paddingVertical: 8,
    }),

    addFieldBtn: (colors) => ({
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingVertical: 10,
        marginTop: 6,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: colors.forms,
        borderStyle: 'dashed',
        justifyContent: 'center',
        _web: {
            transition: 'background-color 0.15s ease, transform 0.15s ease',
            cursor: 'pointer',
            _hover: {
                backgroundColor: colors.formsContainer,
                transform: [{ scale: 1.01 }],
            },
        },
    }),
    addFieldText: (colors, fonts) => ({
        fontSize: 14,
        fontFamily: fonts.body,
        color: colors.forms,
    }),

    addSectionBtn: (colors) => ({
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingVertical: 14,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: colors.forms,
        borderStyle: 'dashed',
        justifyContent: 'center',
        marginTop: 4,
        _web: {
            transition: 'background-color 0.15s ease, transform 0.15s ease',
            cursor: 'pointer',
            _hover: {
                backgroundColor: colors.formsContainer,
                transform: [{ scale: 1.01 }],
            },
        },
    }),
    addSectionText: (colors, fonts) => ({
        fontSize: 15,
        fontFamily: fonts.heading,
        color: colors.forms,
    }),
}));

export default FormBuildTab;
